import { createRef, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Field, Placeholder, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import useContentAccessLevel from 'src/hooks/useContentAccessLevel';

import { ComponentProps, SitecoreContextValue } from 'lib/ComponentProps';

import { parseAccessLevelRouteFields } from 'src/mappers/fields/parseAccessLevelContent';

import { FieldEditFrame } from 'src/utils/customFieldEditFrame';
import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { Button } from 'src/components/basicContent/_Button/Button';

import ResourceNavigationMenuIconSvg from 'public/icons/ResourceNavigationMenu.svg';

import { EditorHelp } from '../_EditorHelp/_EditorHelp';

import styles from './_ThreeTabs.module.css';

type ThreeTabsProps = ComponentProps & {
	fields: {
		Tab1Text: Field<string>;
		Tab2Text: Field<string>;
		HideTab2: Field<boolean>;
		Tab3Text: Field<string>;
		HideTab3: Field<boolean>;
		DatasourceId: string;
	};
};

export default function ThreeTabs(props: ThreeTabsProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const isEditing = IsSitecoreEditing();

	const tabs = useRef<HTMLDivElement | null>(null);

	const [selectedTab, setSelectedTab] = useState(0);
	const [internalMenuState, setInternalMenuState] = useState(false);
	const [externalMenuState, setExternalMenuState] = useState(false);
	const [tabChangeState, setTabChangeState] = useState(false);

	const access = parseAccessLevelRouteFields(sitecoreContext.route.fields);
	const { isAllowedAccessToContent } = useContentAccessLevel(access);

	const placeholders = useMemo(() => {
		const placeholderList = [
			{
				item: (
					<Placeholder
						key="rcnlearn-tab1"
						name="rcnlearn-tab1"
						rendering={props.rendering}
						showNavigationContainer={externalMenuState}
						setShowNavigationContainer={setExternalMenuState}
					/>
				),
				nodeRef: createRef<HTMLDivElement>(),
			},
		];

		if (!props.fields || !props.fields.HideTab2 || !props.fields.HideTab2.value) {
			placeholderList.push({
				item: <Placeholder key="rcnlearn-tab2" name="rcnlearn-tab2" rendering={props.rendering} />,
				nodeRef: createRef<HTMLDivElement>(),
			});
		}

		if (!props.fields || !props.fields.HideTab3 || !props.fields.HideTab3.value) {
			placeholderList.push({
				item: <Placeholder key="rcnlearn-tab3" name="rcnlearn-tab3" rendering={props.rendering} />,
				nodeRef: createRef<HTMLDivElement>(),
			});
		}
		return placeholderList;
	}, [externalMenuState, props.rendering, props.fields]);

	const tabButtons = [props.fields?.Tab1Text];
	if (!props.fields || !props.fields.HideTab2 || !props.fields.HideTab2.value) {
		tabButtons.push(props.fields?.Tab2Text);
	}
	if (!props.fields || !props.fields.HideTab3 || !props.fields.HideTab3.value) {
		tabButtons.push(props.fields?.Tab3Text);
	}

	const disableTabs = !props.fields;

	useEffect(() => {
		if (!tabChangeState) {
			setExternalMenuState(internalMenuState);
		}
	}, [internalMenuState, tabChangeState]);

	useEffect(() => {
		!externalMenuState && setInternalMenuState(false);
	}, [externalMenuState]);

	useEffect(() => {
		const activeTab = placeholders[selectedTab].nodeRef.current;

		if (activeTab && tabs.current) {
			tabs.current.style.height = `${activeTab.offsetHeight}px`;
		}
	}, [placeholders, selectedTab]);

	useEffect(() => {
		const observer = new ResizeObserver(() => {
			const allHeights = placeholders.map((placeholder) => placeholder.nodeRef.current?.offsetHeight);

			if (tabs.current && !allHeights.includes(tabs.current.offsetHeight)) {
				tabs.current.style.height = `${placeholders[selectedTab].nodeRef.current?.offsetHeight}px`;
			}
		});

		placeholders.forEach(({ nodeRef }) => {
			nodeRef.current && observer.observe(nodeRef.current);
		});

		return () => observer.disconnect();
	}, [tabs, selectedTab, placeholders]);

	const TabNav = () => {
		return (
			<FieldEditFrame
				itemId={props.fields?.DatasourceId ?? ''}
				fields={['Tab1Text', 'Tab2Text', 'HideTab2', 'Tab3Text', 'HideTab3']}
				header="Edit Tabs"
			>
				<EditorHelp text="Click here to edit tabs"></EditorHelp>
				<div className={styles.tabNavList}>
					{tabButtons.map((tabButton, index) => (
						<Fragment key={index}>
							{tabButton && tabButton.value && !isEditing ? (
								<Button
									id={index}
									title={tabButton?.value}
									onClick={() => setSelectedTab(index)}
									className={`buttonReset ${styles.tabNavItem} ${selectedTab === index ? styles.active : ''}`}
									disabled={index > 0 && !isAllowedAccessToContent}
								>
									<Text field={tabButton} />
								</Button>
							) : (
								<Text id={index} tag="p" field={tabButton} className={`${styles.tabNavItem} ${isEditing ? 'isEditing' : ''}`} />
							)}
						</Fragment>
					))}
				</div>
			</FieldEditFrame>
		);
	};

	return (
		<section className={`${styles.ThreeTabs} maxWidth`}>
			<EditorHelp text="Three Tabs - START" color={'blue'} />

			<header className={`${styles.stickyHeader} padX`}>
				<div className={`${styles.tabNavListWrapper} ${!disableTabs ? styles.withTabs : ''}`}>
					<Button
						title="Resource Navigation Menu"
						className={`${styles.resourceNavigationMobileMenu} buttonReset`}
						disabled={isEditing}
						onClick={() => {
							selectedTab !== 0 && setSelectedTab(0);
							setInternalMenuState(true);
						}}
					>
						<ResourceNavigationMenuIconSvg className={styles.resourceNavigationMobileMenu} />
					</Button>

					{props.fields && (isEditing || !disableTabs) && <TabNav />}

					{!props.fields && isEditing && (
						<>
							<EditorHelp text="Add a datasource to this tab component to change the text of the tabs" indent />
							<p>Tab 1</p>
							<p>Tab 2</p>
							<p>Tab 3</p>
						</>
					)}
				</div>
			</header>

			{!disableTabs && !isEditing ? (
				<div ref={tabs} className={styles.tabs}>
					<TransitionGroup component={null}>
						{placeholders
							.filter((_placeholder, index: number) => index === selectedTab)
							.map(({ item, nodeRef }) => (
								<CSSTransition
									key={item.key}
									nodeRef={nodeRef}
									timeout={300}
									classNames={{ ...styles }}
									onExit={() => setTabChangeState(true)}
									onExited={() => setTabChangeState(false)}
								>
									<div ref={nodeRef} className={styles.tabContent}>
										{item}
									</div>
								</CSSTransition>
							))}
					</TransitionGroup>
				</div>
			) : (
				<>
					{placeholders.map((placeholder, index) => {
						const tabText = index === 0 ? props.fields.Tab1Text : index === 1 ? props.fields.Tab2Text : props.fields.Tab3Text;

						return (
							<Fragment key={index}>
								<EditorHelp text={`${tabText?.value} Tab - START`} color={'blue'} />

								{placeholder.item}

								<EditorHelp text={`${tabText?.value} Tab - END`} color={'blue'} />
								<p></p>
							</Fragment>
						);
					})}
				</>
			)}
			<EditorHelp text="Three Tabs - END" color={'blue'} />
		</section>
	);
}
